import type { AppDispatch, RootState } from "app/store";
import { renderActions, selectDefaultVisibility } from "features/render";
import { undoRedoActions } from "features/undoRedo";

import { measureActions, selectObjectMeasure } from "./measureSlice";
import { turnOffObjectMeasureXray } from "./objectMeasureXray";

/**
 * Handles the click that follows a complete pair in the object-to-object tool: anywhere on the
 * canvas, object or empty space, it clears the measurement and its x-ray and leaves the tool
 * waiting for a first object again. It does not pick anything itself, so a click meant to look at
 * the result is not also the start of the next measurement.
 *
 * A finished result is undoable, so a stray click only costs an undo. A pair without one - still
 * measuring, or nothing found - is not: the picks are never restored on their own, since a pair
 * left selected would only make the measuring effect measure it again.
 */
export function dismissObjectMeasure(dispatch: AppDispatch, state: RootState) {
    const dismissed = selectObjectMeasure(state);
    const visibilityBefore = selectDefaultVisibility(state);

    // Redo runs against the state undo put back, which is the one captured here.
    const apply = () => {
        dispatch(measureActions.clearObjectMeasure());
        turnOffObjectMeasureXray(dispatch, state);
    };

    apply();

    if (!dismissed.result) {
        return;
    }

    dispatch(
        undoRedoActions.push({
            description: "Clear object to object measurement",
            undoCallback: () => {
                dispatch(measureActions.setObjectMeasure(dismissed));
                dispatch(renderActions.setVisibilityMode(visibilityBefore));
            },
            redoCallback: apply,
        }),
    );
}
